import {
  type CreateReference,
  createCodeableConcept,
  createFhirDateTime,
  createReference,
  getDisplayFromCodableConcepts,
  getFirstCodeFromCodableConcepts,
  getQuantityWithValue,
  getReferenceId,
  isValidReference,
  ObservationCode,
  ObservationMeasurementCode,
  ObservationMeasurementUnits,
  valsOrNone,
} from '@quromedical/fhir-common';
import {
  type ArrayMap,
  compact,
  type Fn,
  first,
  flatten,
  uniq,
  type WithOptional,
} from '@quromedical/utils';
import type { ObservationComponent, ObservationReferenceRange } from 'fhir/r4';
import { PatchPostureCalibrateValue } from '../device-command';
import { UUIDSchema } from '../domain/models';
import { getValidReference } from '../mappings/reference';
import type { WithId } from '../types';
import {
  type BaseTimeSeriesRecord,
  MeasurementName,
  PostureCalibration,
  type VitalsLatestResultMeasures,
} from '../vitals/schema';

import type {
  Observation,
  ObservationPathologyComponent,
  ObservationPregnancy,
  ObservationResult,
  ObservationVitalMeasurement,
} from './schema';

type TimeSeriesRecordWithOptionalSensor = WithOptional<
  BaseTimeSeriesRecord,
  'sensorId'
>;

type LatestResultWithOptionalDevice = Partial<
  Record<MeasurementName, TimeSeriesRecordWithOptionalSensor>
>;

interface MapLatestVitalsToObservationParams {
  vitals: LatestResultWithOptionalDevice;
  subject: fhir4.Reference;
  practitioner: fhir4.Reference;
  effectiveDateTime: string;
  type: ObservationCode.clinicalImpression | ObservationCode.vitals;
}

const measurementNameToCodeMapping: Record<
  MeasurementName,
  ObservationMeasurementCode
> = {
  [MeasurementName.Activity]: ObservationMeasurementCode.Activity,
  [MeasurementName.HeartRate]: ObservationMeasurementCode.HeartRate,
  [MeasurementName.RespiratoryRate]: ObservationMeasurementCode.RespiratoryRate,
  [MeasurementName.SkinTemperature]: ObservationMeasurementCode.SkinTemperature,
  [MeasurementName.CoreTemperature]: ObservationMeasurementCode.CoreTemperature,
  [MeasurementName.OxygenSaturation]:
    ObservationMeasurementCode.OxygenSaturation,
  [MeasurementName.Steps]: ObservationMeasurementCode.Steps,
  [MeasurementName.Posture]: ObservationMeasurementCode.Posture,
  [MeasurementName.Battery]: ObservationMeasurementCode.Battery,
  [MeasurementName.RRI]: ObservationMeasurementCode.RRI,
  [MeasurementName.ECG]: ObservationMeasurementCode.ECG,
  [MeasurementName.PerfusionIndex]: ObservationMeasurementCode.PerfusionIndex,
  [MeasurementName.Glucose]: ObservationMeasurementCode.Glucose,
  [MeasurementName.GlucoseBeforeMeal]:
    ObservationMeasurementCode.GlucoseBeforeMeal,
  [MeasurementName.GlucoseAfterMeal]:
    ObservationMeasurementCode.GlucoseAfterMeal,
  [MeasurementName.GlucoseFasting]: ObservationMeasurementCode.GlucoseFasting,
  [MeasurementName.SystolicArterialBloodPressure]:
    ObservationMeasurementCode.SystolicArterialBloodPressure,
  [MeasurementName.DiastolicArterialBloodPressure]:
    ObservationMeasurementCode.DiastolicArterialBloodPressure,
  [MeasurementName.MeanArterialBloodPressure]:
    ObservationMeasurementCode.MeanArterialBloodPressure,
  [MeasurementName.CombinedArterialBloodPressure]:
    ObservationMeasurementCode.CombinedArterialBloodPressure,
  [MeasurementName.Weight]: ObservationMeasurementCode.Weight,
  [MeasurementName.PostureCalibration]:
    ObservationMeasurementCode.PostureCalibration,
  [MeasurementName.EarlyWarningScore]:
    ObservationMeasurementCode.EarlyWarningScore,
  [MeasurementName.ButtonPress]: ObservationMeasurementCode.ButtonPress,
  [MeasurementName.SensorLowQuality]:
    ObservationMeasurementCode.SensorLowQuality,
  [MeasurementName.Fall]: ObservationMeasurementCode.Fall,
  [MeasurementName.WearStatus]: ObservationMeasurementCode.WearStatus,
  [MeasurementName.DeviceStatus]: ObservationMeasurementCode.DeviceStatus,
};

const codeToMeasurementNameMapping: Record<
  ObservationMeasurementCode,
  MeasurementName
> = {
  [ObservationMeasurementCode.Activity]: MeasurementName.Activity,
  [ObservationMeasurementCode.HeartRate]: MeasurementName.HeartRate,
  [ObservationMeasurementCode.RespiratoryRate]: MeasurementName.RespiratoryRate,
  [ObservationMeasurementCode.SkinTemperature]: MeasurementName.SkinTemperature,
  [ObservationMeasurementCode.CoreTemperature]: MeasurementName.CoreTemperature,
  [ObservationMeasurementCode.OxygenSaturation]:
    MeasurementName.OxygenSaturation,
  [ObservationMeasurementCode.PerfusionIndex]: MeasurementName.PerfusionIndex,
  [ObservationMeasurementCode.Steps]: MeasurementName.Steps,
  [ObservationMeasurementCode.Posture]: MeasurementName.Posture,
  [ObservationMeasurementCode.Battery]: MeasurementName.Battery,
  [ObservationMeasurementCode.RRI]: MeasurementName.RRI,
  [ObservationMeasurementCode.ECG]: MeasurementName.ECG,
  [ObservationMeasurementCode.Glucose]: MeasurementName.Glucose,
  [ObservationMeasurementCode.GlucoseAfterMeal]:
    MeasurementName.GlucoseAfterMeal,
  [ObservationMeasurementCode.GlucoseBeforeMeal]:
    MeasurementName.GlucoseBeforeMeal,
  [ObservationMeasurementCode.GlucoseFasting]: MeasurementName.GlucoseFasting,
  [ObservationMeasurementCode.SystolicArterialBloodPressure]:
    MeasurementName.SystolicArterialBloodPressure,
  [ObservationMeasurementCode.DiastolicArterialBloodPressure]:
    MeasurementName.DiastolicArterialBloodPressure,
  [ObservationMeasurementCode.MeanArterialBloodPressure]:
    MeasurementName.MeanArterialBloodPressure,
  [ObservationMeasurementCode.CombinedArterialBloodPressure]:
    MeasurementName.CombinedArterialBloodPressure,
  [ObservationMeasurementCode.Weight]: MeasurementName.Weight,
  [ObservationMeasurementCode.PostureCalibration]:
    MeasurementName.PostureCalibration,
  [ObservationMeasurementCode.EarlyWarningScore]:
    MeasurementName.EarlyWarningScore,
  [ObservationMeasurementCode.ButtonPress]: MeasurementName.ButtonPress,
  [ObservationMeasurementCode.SensorLowQuality]:
    MeasurementName.SensorLowQuality,
  [ObservationMeasurementCode.Fall]: MeasurementName.Fall,
  [ObservationMeasurementCode.WearStatus]: MeasurementName.WearStatus,
  [ObservationMeasurementCode.DeviceStatus]: MeasurementName.DeviceStatus,
};

export const mapCodeToMeasurementName = (
  code: ObservationMeasurementCode,
): MeasurementName => codeToMeasurementNameMapping[code];

export const mapMeasurementNameToCode = (
  name: MeasurementName,
): ObservationMeasurementCode => measurementNameToCodeMapping[name];

export const getObservationVitals = (
  observation: fhir4.Observation,
): ObservationVitalMeasurement[] => {
  const { component = [], effectiveDateTime, device } = observation;

  const vitals = component.map<ObservationVitalMeasurement | undefined>(
    (com) => {
      const { code, valueQuantity } = com;

      const observationMeasurementCode =
        getFirstCodeFromCodableConcepts<ObservationMeasurementCode>([code]);

      if (!observationMeasurementCode) {
        return undefined;
      }

      const measurementName =
        codeToMeasurementNameMapping[observationMeasurementCode];

      const value = getQuantityWithValue(valueQuantity);

      const hasData = value && measurementName && effectiveDateTime;

      if (!hasData) {
        return undefined;
      }

      const date = new Date(effectiveDateTime);

      return {
        measurementName,
        sensorId: getReferenceId(device),
        ts: date.getTime(),
        value: value.value,
      };
    },
  );

  return compact(vitals);
};

interface VitalData {
  sensorId?: string;
  ts?: string;
  value?: number;
}

export const getObservationVitalsByMeasurementCode = (
  observations: fhir4.Observation[],
  measurementCode: ObservationMeasurementCode,
): VitalData => {
  const vitalData = observations.map(
    ({ component = [], device, effectiveDateTime }) => {
      const res = component.find(({ code }) => code.text === measurementCode);

      if (!res) {
        return undefined;
      }

      return {
        sensorId: getReferenceId(device),
        ts: effectiveDateTime
          ? new Date(effectiveDateTime).getTime()
          : undefined,
        value: res.valueQuantity?.value,
      };
    },
  );

  const result = first(compact(vitalData)) as VitalData;

  return result;
};

export const mapObservationsToLatestVitals = (
  observations: fhir4.Observation[],
): VitalsLatestResultMeasures => {
  const measurementNames = Object.keys(
    measurementNameToCodeMapping,
  ) as MeasurementName[];

  const vitals = measurementNames.reduce((prev, key) => {
    const measurementCode = measurementNameToCodeMapping[key];

    if (!measurementCode) {
      return prev;
    }

    const result = getObservationVitalsByMeasurementCode(
      observations,
      measurementCode,
    );

    if (!result) {
      return prev;
    }

    return {
      ...prev,
      [key]: {
        measurementName: key,
        sensorId: result.sensorId,
        ts: result.ts,
        value: result.value,
      },
    };
  }, {} as VitalsLatestResultMeasures);

  return vitals;
};

const mapMeasurementToObservationComponent: ArrayMap<
  TimeSeriesRecordWithOptionalSensor,
  ObservationComponent
> = ({ measurementName, value }) => {
  const measurementCode = measurementNameToCodeMapping[measurementName];
  const unit = ObservationMeasurementUnits[measurementCode];
  const code = createCodeableConcept(measurementCode);

  return {
    code,
    valueQuantity: {
      value,
      unit,
    },
  };
};

const getDeviceId = (vitals: LatestResultWithOptionalDevice) => {
  const deviceIds = Object.values(vitals).map((obj) => obj.sensorId);
  return first(compact(deviceIds));
};

export const mapLatestVitalsToObservation: Fn<
  MapLatestVitalsToObservationParams,
  fhir4.Observation
> = ({ vitals, subject, practitioner, effectiveDateTime, type }) => {
  const component: ObservationComponent[] = Object.values(
    vitals,
  ).map<ObservationComponent>(mapMeasurementToObservationComponent);

  const deviceId = getDeviceId(vitals);
  const validUUID = UUIDSchema.safeParse(deviceId);
  const device = validUUID.success
    ? createReference('Device', validUUID.data)
    : undefined;

  const observation: fhir4.Observation = {
    subject,
    component,
    effectiveDateTime,
    status: 'final',
    resourceType: 'Observation',
    performer: [practitioner],
    code: createCodeableConcept(type),
    device,
  };

  return observation;
};

export const mapFhirToModelObservation = (
  fhir: fhir4.Observation,
): Partial<Observation> => {
  const {
    status,
    code,
    subject,
    issued,
    performer = [],
    device,
    encounter,
  } = fhir;
  const measures = getObservationVitals(fhir);
  const performers = performer.filter(isValidReference);

  const model: Partial<Observation> = {
    status,
    performers,
    measures,
    code: getFirstCodeFromCodableConcepts<ObservationCode>([code]),
    dateTime: issued,
    patient: getValidReference(subject),
    device: getValidReference(device),
    admission: getValidReference(encounter),
  };
  return model;
};

export const mapFhirToModelObservationWithId = (
  fhir: WithId<fhir4.Observation>,
): WithId<Partial<Observation>> => ({
  ...mapFhirToModelObservation(fhir),
  id: fhir.id,
});

const invertedComparatorMapping: Record<string, string> = {
  '<': '>=',
  '<=': '>',
  '>': '<=',
  '>=': '<',
};

const mapReferenceRange = (ranges?: ObservationReferenceRange[]): string => {
  if (!ranges) {
    return '';
  }
  const test = compact(
    ranges?.map((range) => {
      if (range.low?.extension) {
        const comparator =
          invertedComparatorMapping[
            `${first(range.low?.extension)?.valueString}`
          ];
        return `${comparator} ${range.low.value} ${range.low.unit}`;
      }
      if (range.high?.extension) {
        const comparator =
          invertedComparatorMapping[
            `${first(range.high?.extension)?.valueString}`
          ];
        return `${comparator} ${range.high.value} ${range.high.unit}`;
      }
      if (range.low && range.high) {
        return `${range.low.value} - ${range.high.value} ${range.low.unit}`;
      }
      if (range.low && !range.high) {
        return `${range.low.value} ${range.low.unit}`;
      }
      if (!range.low && range.high) {
        return `${range.high.value} ${range.high.unit}`;
      }
    }),
  );
  return `${test}`;
};

const mapToPathologyReportTable = (
  res?: ObservationComponent[],
): ObservationPathologyComponent[] => {
  const table = res?.map<ObservationPathologyComponent>((component) => {
    const interpretation =
      (component?.interpretation
        ? getDisplayFromCodableConcepts(component?.interpretation)
        : '') || '';

    const result: ObservationPathologyComponent = {
      code: getDisplayFromCodableConcepts([component.code]) || '',
      interpretation,
      valueQuantity: {
        value: component.valueQuantity?.value?.toString(),
        unit: component.valueQuantity?.unit,
      },
      referenceRange: mapReferenceRange(component.referenceRange),
      valueString: component?.valueString,
    };

    return result;
  });

  return flatten(compact(table));
};

export const mapModelToFhirObservationPathologyReport = (
  fhir: WithId<fhir4.Observation>,
): Partial<ObservationResult> => {
  const { status, subject, effectiveDateTime, basedOn, component, code } = fhir;
  const components = mapToPathologyReportTable(component);

  const model: Partial<ObservationResult> = {
    status,
    code: getFirstCodeFromCodableConcepts<ObservationCode>([
      code,
    ]) as ObservationCode.pathologyTestResult,
    basedOn: getValidReference(first(basedOn)),
    components,
    effectiveDateTime,
    patient: getValidReference(subject),
  };

  return { ...model, id: fhir.id };
};

/**
 * The `patient` is optional since in some cases we may create an observation at the device level
 * and not the patient level, e.g. when calibrating a device
 */
export const mapModelToFhirObservation: Fn<
  WithOptional<Observation, 'patient' | 'device'>,
  fhir4.Observation
> = ({
  code,
  dateTime,
  measures,
  patient,
  performers,
  status,
  device,
  admission,
}) => {
  const component = valsOrNone(
    measures.map<ObservationComponent>(mapMeasurementToObservationComponent),
  );
  const effectiveDateTime = getEffectiveDateTimeFromMeasures(measures);
  return {
    resourceType: 'Observation',
    status,
    effectiveDateTime,
    subject: patient,
    performer: performers,
    code: createCodeableConcept(code),
    device,
    component,
    issued: dateTime,
    encounter: admission,
  };
};

export const mapPregnancyToObservationComponent = (
  pregnancy: ObservationPregnancy,
): ObservationComponent[] => {
  const { edd, lactation } = pregnancy;

  const eddComponent = edd
    ? {
        code: createCodeableConcept(ObservationCode.edd),
        valueString: edd,
      }
    : undefined;
  const lactationComponent = lactation
    ? {
        code: createCodeableConcept(ObservationCode.lactation),
        valueBoolean: lactation,
      }
    : undefined;

  return compact([eddComponent, lactationComponent]);
};

interface PregnancyObservation {
  pregnancy: ObservationPregnancy;
  performers: CreateReference[];
  patient: CreateReference;
  encounter: string;
}
export const mapPregnancyToFhirObservation: Fn<
  PregnancyObservation,
  fhir4.Observation
> = ({ pregnancy, patient, performers, encounter }) => {
  const pregnancyObservation: fhir4.Observation = {
    resourceType: 'Observation',
    status: 'final',
    subject: patient,
    performer: performers,
    code: createCodeableConcept(ObservationCode.pregnancy),
    issued: createFhirDateTime(new Date()),
    component: mapPregnancyToObservationComponent(pregnancy),
    valueBoolean: pregnancy.isPregnant,
    encounter: {
      reference: encounter,
    },
  };
  return pregnancyObservation;
};

export const mapFhirObservationToModelPregnancy = (
  fhir: fhir4.Observation,
): ObservationPregnancy | undefined => {
  const eddComponent = fhir.component?.find(
    ({ code }) => code.text === ObservationCode.edd,
  );
  const lactationComponent = fhir.component?.find(
    ({ code }) => code.text === ObservationCode.lactation,
  );

  return {
    edd: eddComponent?.valueString,
    isPregnant: fhir.valueBoolean || false,
    lactation: lactationComponent?.valueBoolean || false,
  };
};

export const getEffectiveDateTimeFromMeasures = (
  measures: ObservationVitalMeasurement[],
) => {
  const effectiveDateTimeArray = measures.map((measure) => measure.ts);

  const uniqEffectiveDateTimes = uniq(effectiveDateTimeArray);

  const uniqEffectiveDateTime =
    uniqEffectiveDateTimes.length !== 1
      ? undefined
      : first(uniqEffectiveDateTimes);

  const effectiveDateTime = uniqEffectiveDateTime
    ? createFhirDateTime(new Date(uniqEffectiveDateTime))
    : undefined;

  return effectiveDateTime;
};

const postureCommandToMeasurementMap: Record<
  PatchPostureCalibrateValue,
  PostureCalibration
> = {
  [PatchPostureCalibrateValue.LayingSupine]: PostureCalibration.LayingSupine,
  [PatchPostureCalibrateValue.Upright]: PostureCalibration.Upright,
};

export const postureCalibrationToMeasurement = (
  time: Date,
  sensorId: string,
  value?: PatchPostureCalibrateValue,
): ObservationVitalMeasurement | undefined => {
  if (typeof value === 'undefined') {
    return undefined;
  }

  return {
    sensorId,
    measurementName: MeasurementName.PostureCalibration,
    ts: time.getTime(),
    value: postureCommandToMeasurementMap[value],
  };
};

export const temperatureCalibrationToMeasurement = (
  time: Date,
  sensorId: string,
  value?: number,
): ObservationVitalMeasurement | undefined => {
  if (typeof value === 'undefined') {
    return undefined;
  }

  return {
    sensorId,
    measurementName: MeasurementName.CoreTemperature,
    ts: time.getTime(),
    value,
  };
};
